import { useMemo } from "react";
import useFetchGraphData from "../useFetchGraphData";
import {
  isComparisonPeriod,
  periodLabel,
  toApiQuery,
} from "../../pages/upstreamDownstream/usdsParams";
import { boxesDomain } from "../../components/charts/usdsBoxPlot/usdsBoxPlotHelpers";

/**
 * Reshapes the API payload into what Nivo's box plot needs.
 *
 * The API returns one entry per reach holding a box per site and year. Nivo
 * wants a flat array of raw values with a group (the site) and a sub group
 * (the period), plus an explicit group order so the upstream-to-downstream
 * sequence is never re-derived from the data.
 *
 * Exported apart from the hook so the reshaping can be tested without standing
 * up react-query.
 *
 * @param {object} data the /api/usds/boxplot response
 * @returns {object|null} reaches ready to draw, or null when there is nothing
 */
export const toBoxplotSeries = (data) => {
  if (!data?.reaches?.length) return null;

  /* One label per period for the whole response. A site whose record starts
     late (USGS Parker's phosphorus begins in WY2017, PC-1's in WY2018) has a
     narrower comparison window than its neighbors; labeling each box from
     its own window made those boxes a third series, with their own slot on the
     axis and their own legend entry. The period is named from the widest
     window present, and each box keeps its own coverage for the tooltip. */
  const comparisonRange = data.reaches
    .flatMap((r) => r.boxes)
    .filter((b) => isComparisonPeriod(b.collectYear))
    .reduce(
      (acc, b) => ({
        min: Math.min(acc.min, b.waterYearMin ?? acc.min),
        max: Math.max(acc.max, b.waterYearMax ?? acc.max),
      }),
      { min: Infinity, max: -Infinity }
    );
  const labelFor = (box) =>
    isComparisonPeriod(box.collectYear) && Number.isFinite(comparisonRange.min)
      ? periodLabel(box.collectYear, comparisonRange.min, comparisonRange.max)
      : periodLabel(box.collectYear, box.waterYearMin, box.waterYearMax);

  const reaches = data.reaches.map((reach) => {
    const sites = [];
    const seenSites = new Set();
    const subGroups = [];
    const seenPeriods = new Set();
    const values = [];
    const boxesByKey = new Map();

    reach.boxes.forEach((box) => {
      if (!seenSites.has(box.locationid)) {
        seenSites.add(box.locationid);
        sites.push({
          locationid: box.locationid,
          locationName: box.locationName,
          locationDesc: box.locationDesc,
          streamOrder: box.streamOrder,
          isSynoptic: box.isSynoptic,
        });
      }

      const period = labelFor(box);
      if (!seenPeriods.has(period)) {
        seenPeriods.add(period);
        subGroups.push({
          label: period,
          isComparison: isComparisonPeriod(box.collectYear),
        });
      }

      /* How much of the box is censored. The pipeline stores a non-detect as
         zero, so a mostly non-detect box is a statement about the detection
         limit, not the stream, and the chart marks it. */
      const nondetectCount = box.values.filter((v) => v.nondetect).length;
      boxesByKey.set(`${box.locationName}|${period}`, {
        ...box,
        period,
        nondetectCount,
        mostlyNondetect:
          box.values.length > 0 && nondetectCount * 2 > box.values.length,
      });

      box.values.forEach((v) => {
        values.push({
          site: box.locationName,
          period,
          value: v.resultValue,
          collectDate: v.collectDate,
          isOutlier: v.isOutlier,
          isExtremeOutlier: v.isExtremeOutlier,
          nondetect: v.nondetect,
        });
      });
    });

    /* The selected year is always the first sub group, so its color and its
       place in the legend stay put when the comparison period changes. */
    subGroups.sort((a, b) => Number(a.isComparison) - Number(b.isComparison));

    /* The span the chart draws, 5th to 95th percentile across the reach. */
    const dataDomain = boxesDomain(reach.boxes);

    return {
      reachGroupid: reach.reachGroupid,
      reachLabel: reach.reachLabel,
      graphYaxisMin: reach.graphYaxisMin,
      graphYaxisMax: reach.graphYaxisMax,
      groups: sites.map((s) => s.locationName),
      subGroups: subGroups.map((s) => s.label),
      dataDomain,
      sites,
      boxes: Array.from(boxesByKey.values()),
      boxesByKey,
      values,
      synopticSites: sites
        .filter((s) => s.isSynoptic)
        .map((s) => s.locationName),
    };
  });

  /* When the y axis is locked, every reach shares one domain so the eye can
     carry a value from one chart to the next: the lowest low and highest high
     of the reaches being drawn. */
  const lockedDomain = reaches.reduce(
    (acc, r) => ({
      min: Math.min(acc.min, r.dataDomain?.min ?? acc.min),
      max: Math.max(acc.max, r.dataDomain?.max ?? acc.max),
    }),
    { min: Infinity, max: -Infinity }
  );

  return {
    reaches,
    parameter: data.parameter,
    selectedYear: data.selectedYear,
    comparison: data.comparison,
    comparisonLabel: data.comparisonLabel,
    comparisonRange: Number.isFinite(comparisonRange.min)
      ? comparisonRange
      : null,
    lockedDomain:
      Number.isFinite(lockedDomain.min) && Number.isFinite(lockedDomain.max)
        ? lockedDomain
        : null,
  };
};

/**
 * Feeds the upstream-to-downstream box plots.
 * @param {object} params parsed URL state
 * @param {number} year the water year to draw, after defaults are resolved
 */
export default function useUsdsBoxplotSeries(params, year) {
  const enabled = Number.isInteger(year);

  const {
    data,
    isLoading, // true only with nothing cached; isFetching would unmount the chart on every refetch
    isError,
  } = useFetchGraphData(
    enabled ? toApiQuery(params, year) : null,
    "usds/boxplot"
  );

  return useMemo(
    () => ({
      reaches: [],
      parameter: null,
      ...(toBoxplotSeries(data) ?? {}),
      isLoading,
      isError,
    }),
    [data, isLoading, isError]
  );
}
